import { isVaultDataDecrypted, isVaultDataEmpty } from '@onefootprint/core';
import type { DataIdentifier } from '@onefootprint/request-types';
import partition from 'lodash/partition';
import type { AutoDecryptField } from '@/entity/hooks/use-auto-decrypt-controls';
import type { EntityVault } from '@/types/vault';

const GROUPED_PREFIX_FIELDS: AutoDecryptField[] = ['document', 'custom', 'investor_profile', 'card', 'bank'] as const;
const FIELDSET_DIS = {
  basic: ['id.first_name', 'id.middle_name', 'id.last_name', 'id.email', 'id.phone_number'],
  identity: ['id.dob', 'id.ssn4', 'id.ssn9'],
  address: ['id.address_line1', 'id.address_line2', 'id.city', 'id.state', 'id.zip', 'id.country'],
  legalStatus: ['id.us_legal_status', 'id.visa_kind', 'id.visa_expiration_date', 'id.nationality', 'id.citizenships'],
  businessBasic: ['business.name', 'business.dba', 'business.website', 'business.phone_number', 'business.tin'],
  businessAddress: [
    'business.address_line1',
    'business.address_line2',
    'business.city',
    'business.state',
    'business.zip',
    'business.country',
  ],
} as const;

/**
 * Formats fields checked during decryption to be used by auto-decryption.
 * Includes logic for adding related empty fields when all non-empty fields in a group are selected.
 */
const getAutoDecryptFields = ({
  selectedFields,
  excludedPrefixes,
  vaultData,
}: {
  selectedFields: DataIdentifier[];
  excludedPrefixes: AutoDecryptField[];
  vaultData: EntityVault;
}): AutoDecryptField[] => {
  // Get grouped prefix fields (document, custom, etc.)
  const groupedFields = getGroupedPrefixFields(selectedFields);

  // Filter out grouped prefix fields and excluded prefixes
  const individualFields = filterIndividualFields(selectedFields, excludedPrefixes);

  // Add empty fields from fieldsets where all non-empty fields are selected
  const emptyFieldsFromFieldsets = getAllEmptyFieldsFromFieldsets(individualFields, vaultData);

  const allFields = [...individualFields, ...groupedFields, ...emptyFieldsFromFieldsets];
  return allFields;
};

/**
 * Filters out grouped prefix fields and excluded prefixes from selected fields
 */
const filterIndividualFields = (
  selectedFields: DataIdentifier[],
  excludedPrefixes: AutoDecryptField[],
): AutoDecryptField[] =>
  selectedFields.filter(
    field =>
      !GROUPED_PREFIX_FIELDS.some(prefix => field.startsWith(prefix)) &&
      !excludedPrefixes.some(prefix => field.startsWith(prefix)),
  );

/**
 * Returns prefix-based field groups that should be added if any matching fields are selected
 * eg: if 'document.driver_license.selfie' is selected, 'document' will be added to the auto-decrypt fields
 */
const getGroupedPrefixFields = (selectedFields: DataIdentifier[]): AutoDecryptField[] =>
  GROUPED_PREFIX_FIELDS.filter(prefix => selectedFields.some(field => field.startsWith(prefix)));

/**
 * Gets all empty fields from all fieldsets where all non-empty fields are selected
 */
const getAllEmptyFieldsFromFieldsets = (
  selectedFields: AutoDecryptField[],
  vaultData: EntityVault,
): DataIdentifier[] => {
  const allFieldsetFields = Object.values(FIELDSET_DIS);
  return allFieldsetFields.flatMap(fieldsetDis =>
    getEmptyFieldsIfAllNonEmptyIncluded({
      selectedFields,
      fieldsetDis,
      vaultData,
    }),
  );
};

/**
 * Returns empty fields from a fieldset if all non-empty fields in that fieldset are already selected
 * eg: if all Basic data fields are selected, except for 'id.middle_name' which is empty, this will return ['id.middle_name']
 */
const getEmptyFieldsIfAllNonEmptyIncluded = ({
  selectedFields,
  fieldsetDis,
  vaultData,
}: {
  selectedFields: AutoDecryptField[];
  fieldsetDis: readonly DataIdentifier[];
  vaultData: EntityVault;
}): DataIdentifier[] => {
  const [emptyFields, nonEmptyFields] = partition(fieldsetDis, field => isVaultDataEmpty(vaultData[field]));

  // Only add empty fields if there are non-empty fields in this fieldset and all of them are selected
  if (nonEmptyFields.length === 0) {
    return [];
  }

  const areAllNonEmptyFieldsSelected = nonEmptyFields.every(
    field => selectedFields.includes(field) || (field === 'id.first_name' && isVaultDataDecrypted(vaultData[field])), // Because first name is decrypted by default
  );

  return areAllNonEmptyFieldsSelected ? emptyFields : [];
};

export default getAutoDecryptFields;
